import { createActionBarEffectFeature } from '../../features/selections/create-action-bar-effect-feature.ts';
import { createAifcDebugSelectionsEffectFeature } from '../../features/selections/create-aifc-debug-selections-effect-feature.ts';
import { createApplyFillEffectFeature } from '../../features/selections/create-apply-fill-effect-feature.ts';
import { createCellEditingEffectFeature } from '../../features/selections/create-cell-editing-effect-feature.ts';
import { createCheckboxCellKeyboardInputFeature } from '../../features/selections/create-checkbox-cell-keyboard-input-feature.ts';
import { createCheckboxEffectFeature } from '../../features/selections/create-checkbox-effect-feature.ts';
import { createClearCellsEffectFeature } from '../../features/selections/create-clear-cells-effect-feature.ts';
import { createContextMenuEffectFeature } from '../../features/selections/create-context-menu-effect-feature.ts';
import { createContextMenuInputFeature } from '../../features/selections/create-context-menu-input-feature.ts';
import { createDeleteIntentHighlightEffectFeature } from '../../features/selections/create-delete-intent-highlight-effect-feature.ts';
import { createFillHandleEffectFeature } from '../../features/selections/create-fill-handle-effect-feature.ts';
import { createFillPreviewEffectFeature } from '../../features/selections/create-fill-preview-effect-feature.ts';
import { createFocusBoxEffectFeature } from '../../features/selections/create-focus-box-effect-feature.ts';
import { createHighlightsEffectFeature } from '../../features/selections/create-highlights-effect-feature.ts';
import { createMouseInputFeature } from '../../features/selections/create-mouse-input-feature.ts';
import { createPresenceEffectFeature } from '../../features/selections/create-presence-effect-feature.ts';
import { createProjectionChangesInputFeature } from '../../features/selections/create-projection-changes-input-feature.ts';
import { createPublishRovoEffectFeature } from '../../features/selections/create-publish-rovo-effect-feature.ts';
import { createSelectionsPingEffectFeature } from '../../features/selections/create-selections-ping-effect-feature.ts';
import { createTableCellActionsKeyboardInputFeature } from '../../features/selections/create-table-cell-actions-keyboard-input-feature.ts';
import { createTableNavigationKeyboardInputFeature } from '../../features/selections/create-table-navigation-keyboard-input-feature.ts';
import { createTableTabNavigationKeyboardInputFeature } from '../../features/selections/create-table-tab-navigation-keyboard-input-feature.ts';
import { createTimelineColumnSelectionPingEffectFeature } from '../../features/selections/create-timeline-column-selection-ping-effect-feature.ts';
import { createTimelineRowSelectionHandlePingEffectFeature } from '../../features/selections/create-timeline-row-selection-handle-ping-effect-feature.ts';
import { type SelectionFeature } from '../../features/types.ts';
import { type SelectionFeaturesConfig, type ModeConfig } from '../types.ts';

/**
 * The default selection features builder — shared by all modes that use
 * the standard feature set. Each mode config can override this if needed.
 */
export const buildDefaultSelectionFeatures: ModeConfig['buildSelectionFeatures'] = (
  deps,
) => {
  const modeFeatures: SelectionFeaturesConfig = {
    inputs: [
      createMouseInputFeature(deps, deps.mouseInputAdapter),
      createTableNavigationKeyboardInputFeature({
        sharedLoop: deps.sharedLoop,
        adapter: deps.databaseAdapter,
        onSelectionUiInteraction: deps.onSelectionUiInteraction,
      }),
      createTableTabNavigationKeyboardInputFeature({
        sharedLoop: deps.sharedLoop,
        adapter: deps.databaseAdapter,
        onSelectionUiInteraction: deps.onSelectionUiInteraction,
      }),
      createCheckboxCellKeyboardInputFeature(deps),
      createTableCellActionsKeyboardInputFeature(deps),
      createContextMenuInputFeature({
        onSelectionUiInteraction: deps.onSelectionUiInteraction,
      }),
    ],
    effects: [
      /**
       * Visuals
       */
      createHighlightsEffectFeature(),
      createFocusBoxEffectFeature(),
      createFillHandleEffectFeature(deps),
      createFillPreviewEffectFeature(),
      createDeleteIntentHighlightEffectFeature(),

      /**
       * UI Components
       */
      createAifcDebugSelectionsEffectFeature(),
      createActionBarEffectFeature(deps),
      createCellEditingEffectFeature(),
      createContextMenuEffectFeature(deps),
      createCheckboxEffectFeature(deps),
      createSelectionsPingEffectFeature(),
      createTimelineColumnSelectionPingEffectFeature(),
      createTimelineRowSelectionHandlePingEffectFeature(),

      /**
       * DB Mutations
       */
      createApplyFillEffectFeature(deps),
      createClearCellsEffectFeature(deps),
    ],
  };

  // Always-on infrastructure features
  const alwaysOnFeatures: SelectionFeature[] = [
    createProjectionChangesInputFeature(deps),
    createPresenceEffectFeature(deps),
    createPublishRovoEffectFeature(),
  ];

  return { modeFeatures, alwaysOnFeatures };
};
